import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  Calendar,
  Save,
  RotateCcw
} from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import sacMasterService from '../../services/sacMasterService'
import RevisionModal from '../../components/RevisionModal/RevisionModal'
import '../../components/RevisionModal/RevisionModal.css'

const SACMasterEdit = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [formData, setFormData] = useState({
    sacCode: '',
    description: '',
    gstRate: '0',
    igstRate: '0',
    sgstRate: '0',
    cgstRate: '0',
    utgstRate: '0',
    revisionNo: '',
    revisionDate: new Date().toISOString().split('T')[0],
    status: 'Active'
  })
  const [sacData, setSacData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showRevisionModal, setShowRevisionModal] = useState(false)
  const [pendingFormData, setPendingFormData] = useState(null)

  useEffect(() => {
    if (location.state?.sacData) {
      const { sacData } = location.state
      setSacData(sacData)
      setFormData({
        sacCode: sacData.sacCode || '',
        description: sacData.description || '',
        gstRate: sacData.gstRate?.toString() || '0',
        igstRate: sacData.igstRate?.toString() || '0',
        cgstRate: sacData.cgstRate?.toString() || '0',
        sgstRate: sacData.sgstRate?.toString() || '0',
        utgstRate: sacData.utgstRate?.toString() || '0',
        revisionNo: sacData.revisionNo || '',
        revisionDate: sacData.revisionDate ? new Date(sacData.revisionDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        status: sacData.status || 'Active'
      })
      setLoading(false)
    } else {
      toast.error('No SAC data provided for editing')
      navigate('/sales/sac-master')
    }
  }, [location.state, navigate])

  const handleInputChange = (e) => {
    const { name, value, type } = e.target
    
    setFormData(prev => {
      const updatedData = {
        ...prev,
        [name]: type === 'number' ? (parseFloat(value) || 0) : value
      }
      
      // Auto-calculate tax rates when GST Rate changes
      if (name === 'gstRate' && type === 'number' && !isNaN(parseFloat(value))) {
        const gstRate = parseFloat(value) || 0
        updatedData.igstRate = gstRate
        updatedData.cgstRate = gstRate / 2
        updatedData.sgstRate = gstRate / 2
        updatedData.utgstRate = gstRate / 2
      }
      
      return updatedData
    })
  }

  const validateForm = () => {
    if (!formData.sacCode.trim()) {
      toast.error('SAC Code is required')
      return false
    }
    if (!/^\d{6}$/.test(formData.sacCode.trim())) {
      toast.error('SAC Code must be exactly 6 digits')
      return false
    }
    if (!formData.description.trim()) {
      toast.error('Description of Service is required')
      return false
    }
    if (parseFloat(formData.gstRate) < 0 || parseFloat(formData.gstRate) > 100) {
      toast.error('GST Rate must be between 0 and 100')
      return false
    }
    if (parseFloat(formData.igstRate) < 0 || parseFloat(formData.igstRate) > 100) {
      toast.error('IGST Rate must be between 0 and 100')
      return false
    }
    if (parseFloat(formData.sgstRate) < 0 || parseFloat(formData.sgstRate) > 100) {
      toast.error('SGST Rate must be between 0 and 100')
      return false
    }
    if (parseFloat(formData.cgstRate) < 0 || parseFloat(formData.cgstRate) > 100) {
      toast.error('CGST Rate must be between 0 and 100')
      return false
    }
    if (parseFloat(formData.utgstRate) < 0 || parseFloat(formData.utgstRate) > 100) {
      toast.error('UTGST Rate must be between 0 and 100')
      return false
    }
    if (!formData.revisionDate) {
      toast.error('Revision Date is required')
      return false
    }
    return true
  }

  const handleSave = async () => {
    if (!validateForm()) {
      return
    }

    // Store the form data and show revision modal
    setPendingFormData(formData)
    setShowRevisionModal(true)
  }

  const handleRevisionSave = async (revisionInfo) => {
    try {
      toast.info('Updating SAC Master record...')
      const response = await sacMasterService.updateSAC(sacData.id, {
        sacCode: pendingFormData.sacCode,
        description: pendingFormData.description,
        gstRate: parseFloat(pendingFormData.gstRate),
        igstRate: parseFloat(pendingFormData.igstRate),
        sgstRate: parseFloat(pendingFormData.sgstRate),
        cgstRate: parseFloat(pendingFormData.cgstRate),
        utgstRate: parseFloat(pendingFormData.utgstRate),
        revisionNo: revisionInfo.revisionNo,
        revisionDate: revisionInfo.revisionDate,
        status: pendingFormData.status,
        revisionInfo: revisionInfo
      })

      if (response.success) {
        toast.success('SAC Master record updated successfully!', { autoClose: 2000 })
        navigate('/sales/sac-master')
      } else {
        toast.error(response.message || 'Failed to update SAC Master record')
      }
    } catch (error) {
      console.error('Error updating SAC Master record:', error)
      if (error.response?.status === 400 && error.response.data?.message.includes('SAC code already exists')) {
        toast.error('SAC Code already exists. Please use a different code.')
      } else if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to perform this action.')
      } else if (error.response?.status === 404) {
        toast.error('SAC Master record not found')
      } else {
        toast.error('Failed to update SAC Master record. Please try again.')
      }
    }
  }

  const handleReset = () => {
    if (sacData) {
      setFormData({
        sacCode: sacData.sacCode || '',
        description: sacData.description || '',
        gstRate: sacData.gstRate?.toString() || '0',
        igstRate: sacData.igstRate?.toString() || '0',
        cgstRate: sacData.cgstRate?.toString() || '0',
        sgstRate: sacData.sgstRate?.toString() || '0',
        utgstRate: sacData.utgstRate?.toString() || '0',
        revisionNo: sacData.revisionNo || '',
        revisionDate: sacData.revisionDate ? new Date(sacData.revisionDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        status: sacData.status || 'Active'
      })
      toast.info('Form reset to original values')
    }
  }

  const handleBack = () => {
    navigate('/sales/sac-master')
  }

  const breadcrumbItems = [
    { label: 'Sales', path: '/sales', onClick: () => navigate('/sales') },
    { label: 'Masters', path: '/sales?tab=masters', onClick: () => navigate('/sales?tab=masters') },
    { label: 'SAC Master', path: '/sales/sac-master', onClick: () => navigate('/sales/sac-master') },
    { label: 'Edit', path: `/sales/sac-master/edit/${sacData?.id}`, isCurrent: true }
  ]

  if (loading) {
    return (
      <div className="sales-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading SAC Master data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>SAC Master</h1>
          <p>Manage Services Accounting Code master data</p>
        </div>
      </div>

      {/* Form Section */}
      <div className="form-container">
        <div className="sac-form-header">
          <h2>SAC Master</h2>
        </div>
          
        <form className="form-content">
          {/* Row 1: SAC Code, Description, GST Rate */}
          <div className="grid-row-3">
            <div className="form-group">
              <label htmlFor="sacCode" className="form-label required">
                SAC Code
              </label>
              <input
                type="text"
                id="sacCode"
                name="sacCode"
                value={formData.sacCode}
                onChange={handleInputChange}
                onKeyPress={(e) => {
                  if (!/[0-9]/.test(e.key)) {
                    e.preventDefault()
                  }
                }}
                className="form-input"
                placeholder="Enter 6-digit SAC Code"
                maxLength="6"
                pattern="[0-9]{6}"
              />
            </div>

            <div className="form-group">
              <label htmlFor="description" className="form-label required">
                Description of Service
              </label>
              <input
                type="text"
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter service description"
              />
            </div>

            <div className="form-group">
              <label htmlFor="gstRate" className="form-label required">
                GST Rate %
              </label>
              <input
                type="number"
                id="gstRate"
                name="gstRate"
                value={formData.gstRate}
                onChange={handleInputChange}
                className="form-input"
                step="0.01"
                min="0"
              />
            </div>
          </div>

          {/* Row 2: IGST Rate, CGST Rate, SGST Rate */}
          <div className="grid-row-3">
            <div className="form-group">
              <label htmlFor="igstRate" className="form-label required">
                IGST Rate %
              </label>
              <input
                type="number"
                id="igstRate"
                name="igstRate"
                value={formData.igstRate}
                onChange={handleInputChange}
                className="form-input"
                step="0.01"
                min="0"
              />
            </div>

            <div className="form-group">
              <label htmlFor="cgstRate" className="form-label required">
                CGST Rate %
              </label>
              <input
                type="number"
                id="cgstRate"
                name="cgstRate"
                value={formData.cgstRate}
                onChange={handleInputChange}
                className="form-input"
                step="0.01"
                min="0"
              />
            </div>

            <div className="form-group">
              <label htmlFor="sgstRate" className="form-label required">
                SGST Rate %
              </label>
              <input
                type="number"
                id="sgstRate"
                name="sgstRate"
                value={formData.sgstRate}
                onChange={handleInputChange}
                className="form-input"
                step="0.01"
                min="0"
              />
            </div>
          </div>

          {/* Row 3: UTGST Rate, Revision No, Revision Date */}
          <div className="grid-row-3">
            <div className="form-group">
              <label htmlFor="utgstRate" className="form-label required">
                UTGST Rate %
              </label>
              <input
                type="number"
                id="utgstRate"
                name="utgstRate"
                value={formData.utgstRate}
                onChange={handleInputChange}
                className="form-input"
                step="0.01"
                min="0"
              />
            </div>

            <div className="form-group">
              <label htmlFor="revisionNo" className="form-label">
                Current Version
              </label>
              <input
                type="text"
                id="revisionNo"
                name="revisionNo"
                value={`Rev ${sacData?.currentRevision || 1}`}
                className="form-input"
                disabled
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="revisionDate" className="form-label required">
                Revision Date
              </label>
              <div className="date-input-wrapper">
                <input
                  type="text"
                  id="revisionDate"
                  name="revisionDate"
                  value={formData.revisionDate}
                  onChange={handleInputChange}
                  className="form-input date-input"
                  placeholder="DD-MM-YYYY"
                />
                <Calendar size={16} className="calendar-icon" />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="form-actions">
            <div className="status-control">
              <label htmlFor="status" className="status-label">Status</label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="status-dropdown"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
              <div className={`status-indicator ${formData.status === 'Active' ? 'status-active' : 'status-inactive'}`}></div>
            </div>
            <div className="action-buttons">
              <button
                type="button"
                onClick={handleReset}
                className="btn-secondary"
              >
                <RotateCcw size={16} />
                Reset
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="btn-primary"
              >
                <Save size={16} />
                Update
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Revision Modal */}
      <RevisionModal
        isOpen={showRevisionModal}
        onClose={() => setShowRevisionModal(false)}
        onSave={handleRevisionSave}
        currentRevision={sacData?.currentRevision || 1}
      />
    </div>
  )
}

export default SACMasterEdit
